import React, { useState } from 'react';
import { X, Calendar, User, Tag, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const TaskDetailModal = ({ task, isOpen, onClose, onUpdate, onDelete }) => {
  const [editedTask, setEditedTask] = useState(task);

  const handleSave = () => {
    onUpdate(editedTask);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      onDelete(task.id);
    }
  };

  // Helper: derive initials from a full name (e.g. "Marvay Taylor" → "MT", "Akaylah" → "AK")
  const initialsFor = (name) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const assigneeGroups = [
    {
      label: 'Leadership',
      options: [
        { value: 'Brittany Chandler', initials: 'BC', role: 'CEO' },
        { value: 'Cody Stevens',      initials: 'CS', role: 'CFO' },
        { value: 'Yolanda Chandler',  initials: 'YC', role: 'COO' },
        { value: 'Chenise DeLoach',   initials: 'CD', role: 'Director' },
        { value: 'Lauren Simmons',    initials: 'LS', role: 'Director' },
      ],
    },
    {
      label: '3.1 RSS Staff',
      options: [
        { value: 'Marvay Taylor',  initials: initialsFor('Marvay Taylor'),  role: 'RSS' },
        { value: 'Hurieya Morton', initials: initialsFor('Hurieya Morton'), role: 'RSS' },
        { value: 'Keisha Bailey',  initials: initialsFor('Keisha Bailey'),  role: 'RSS' },
        { value: 'Akaylah',        initials: initialsFor('Akaylah'),        role: 'RSS' },
        { value: 'Senita',         initials: initialsFor('Senita'),         role: 'RSS' },
        { value: 'Doreena',        initials: initialsFor('Doreena'),        role: 'RSS' },
        { value: 'Brittany',       initials: initialsFor('Brittany'),       role: 'RSS' },
      ],
    },
  ];

  // Flat list for the value→initials lookup on selection change
  const assigneeOptions = assigneeGroups.flatMap((g) => g.options);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Task Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Title */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Title</label>
            <Input
              value={editedTask.title}
              onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
              placeholder="Task title"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Description</label>
            <Textarea
              value={editedTask.description}
              onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })}
              placeholder="Task description"
              rows={4}
            />
          </div>

          {/* Priority & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Priority</label>
              <Select
                value={editedTask.priority}
                onValueChange={(value) => setEditedTask({ ...editedTask, priority: value })}
              >
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background border border-border shadow-lg z-50">
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select
                value={editedTask.status}
                onValueChange={(value) => setEditedTask({ ...editedTask, status: value })}
              >
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background border border-border shadow-lg z-50">
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Assignee */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Assignee</label>
            <Select
              value={editedTask.assignee}
              onValueChange={(value) => {
                const selected = assigneeOptions.find(a => a.value === value);
                setEditedTask({
                  ...editedTask,
                  assignee: value,
                  assignee_initials: selected?.initials || ''
                });
              }}
            >
              <SelectTrigger className="bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background border border-border shadow-lg z-50">
                {assigneeGroups.map((group) => (
                  <SelectGroup key={group.label}>
                    <SelectLabel className="text-xs uppercase tracking-wide text-muted-foreground">
                      {group.label}
                    </SelectLabel>
                    {group.options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <span className="font-medium">{option.value}</span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {option.initials} · {option.role}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Due Date</label>
            <Input
              type="date"
              value={editedTask.due_date || ''}
              onChange={(e) => setEditedTask({ ...editedTask, due_date: e.target.value })}
            />
          </div>

          {/* Department */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Department</label>
            <Input
              value={editedTask.department}
              onChange={(e) => setEditedTask({ ...editedTask, department: e.target.value })}
              placeholder="Department"
            />
          </div>

          {/* Tags */}
          {editedTask.tags && editedTask.tags.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Tags</label>
              <div className="flex flex-wrap gap-2">
                {editedTask.tags.map((tag, index) => (
                  <Badge key={index} variant="secondary">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaskDetailModal;
